import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMqttConnected, useMqttTopic } from '../mqtt.jsx'
import { useHillActivity } from '../useHillActivity.js'
import { sortClasses } from '../classes.js'
import { sortSessionCodes, sessionLabel, isRunPB } from '../sessions.js'
import { fmtTime, fmtSessionTime, gateColumns, gateValueFor, gateKey } from '../format.js'

const EMPTY_CLASSES = []
const EMPTY_SPLITS = []
const EMPTY_QUALIFIERS = []

/** No real run on any hill NHCA times takes anywhere near this long. A
 * `started` with no matching `finished`/`flagged` within it means the run
 * itself is over but the closing event got lost (a dropped MQTT message,
 * hardware fault, or a rider who's stopped on the hill with no red flag
 * thrown yet) - without a cap the hero clock counts up forever, which reads
 * to a commentator as "still on a genuinely long run" rather than "we've
 * lost track of this one". */
const STALL_SECONDS = 120

/** `hill.split` events only carry a raw gate number + type, not a field name
 * or label - match against `venue.splits` (which has both) for the real
 * venue-specific label, same fallback NIGAN_MQTT/web/live.html uses
 * (getSplitLabel/splitLabels) when no venue mapping is known yet. */
function splitLabel(gate, gateType, venueSplits) {
  if (gateType === 'S') return 'Speed'
  const known = venueSplits.find((g) => g.gate === gate)
  return known?.label || `Split ${gate}`
}

/**
 * "That's 0.2s slower than they did in practice" - the comparison a
 * commentator actually wants: a given time against the rider's OWN best
 * time from any OTHER session so far today (not personalBest, which is a
 * historical record from before this event). Reads straight off the
 * retained `results` topic's per-competitor `sessions` map, so it needs no
 * new backend/wire data. Returns null if there's nothing to compare
 * against yet (first recorded time this event) or no time to compare.
 */
function bestPriorSessionDelta(sessions, excludeCode, time) {
  if (time == null || !sessions) return null
  const priorTimes = Object.entries(sessions).filter(([code, s]) => code !== excludeCode && s.time)
  if (!priorTimes.length) return null
  const [bestCode, bestSession] = priorTimes.reduce((min, entry) => (entry[1].time < min[1].time ? entry : min))
  return { code: bestCode, delta: time - bestSession.time }
}

function priorBestDelta(card, competitor) {
  return card.session ? bestPriorSessionDelta(competitor?.sessions, card.session, card.finalTime) : null
}

/**
 * Same "own improvement, this event" idea as bestPriorSessionDelta above,
 * but per-split rather than per-overall-time - the rider's own best value
 * at one gate `field`, from any OTHER recorded session. There's no
 * historical (pre-event) sector data to compare against and never will be
 * (only overall best times get recorded long-term, see personal_bests),
 * so this is deliberately event-scoped only, not a real "personal best."
 * Naturally shows nothing in first practice - a rider's first-ever
 * recorded run this event has no OTHER session to compare against, so
 * `best` stays null and nothing highlights, which is correct: literally
 * everyone's P1 splits would trivially "beat" a record that doesn't exist
 * yet, so highlighting them wouldn't tell a commentator anything. Becomes
 * meaningful from their second recorded run onward.
 */
function bestPriorGateValue(sessions, excludeCode, field, gateType) {
  if (!sessions) return null
  let best = null
  for (const [code, s] of Object.entries(sessions)) {
    if (code === excludeCode) continue
    const v = gateValueFor(s, field)
    if (v == null) continue
    best = best == null ? v : gateType === 'S' ? Math.max(best, v) : Math.min(best, v)
  }
  return best
}

/** Rows/gate-columns/fastest-so-far for one session code - called twice in
 * CommentatorPage: once for whatever's LATEST (drives hero-card purple/PB
 * and the Overall standings), and once for whichever session the
 * commentator has picked to VIEW in the session table. These can differ
 * once a historical session is selected - hero-card highlighting should
 * always reflect what's actually live, not whatever the commentator
 * happens to be browsing. */
function useSessionData(classes, sessionCode, venueSplits) {
  const rows = useMemo(() => {
    if (!sessionCode) return []
    const result = []
    classes.forEach((c) => {
      c.competitors.forEach((r) => {
        if (r.sessions && Object.prototype.hasOwnProperty.call(r.sessions, sessionCode)) {
          result.push({ ...r, className: c.className })
        }
      })
    })
    result.sort((a, b) => {
      const at = a.sessions[sessionCode]?.time || null
      const bt = b.sessions[sessionCode]?.time || null
      if (at == null && bt == null) return 0
      if (at == null) return 1
      if (bt == null) return -1
      return at - bt
    })
    return result
  }, [classes, sessionCode])

  const cols = useMemo(() => (sessionCode ? gateColumns(rows, sessionCode, venueSplits) : []), [rows, sessionCode, venueSplits])

  const stats = useMemo(() => {
    if (!sessionCode) return null
    const bestAtField = Object.fromEntries(
      cols.map((c) => [
        c.field,
        rows.reduce((best, r) => {
          const v = gateValueFor(r.sessions[sessionCode], c.field)
          if (v == null) return best
          if (best == null) return v
          return c.type === 'S' ? Math.max(best, v) : Math.min(best, v)
        }, null),
      ]),
    )
    const bestTime = rows.reduce((min, r) => {
      const t = r.sessions[sessionCode]?.time
      return t && (min == null || t < min) ? t : min
    }, null)
    return { bestAtField, bestTime }
  }, [rows, cols, sessionCode])

  return { rows, cols, stats }
}

/** Green = faster than their own prior best this event, amber = slower. */
function DeltaVsPrior({ delta }) {
  if (!delta) return null
  return (
    <span className={`delta-vs-prior ${delta.delta <= 0 ? 'faster' : 'slower'}`}>
      {delta.delta >= 0 ? '+' : ''}
      {delta.delta.toFixed(2)}s vs {delta.code}
    </span>
  )
}

// forwardRef so the parent can measure/animate this card's position when it
// moves between the top and bottom slot (see the FLIP effect in
// CommentatorPage below) - the ref needs to land on the actual root
// element, not a wrapper, for getBoundingClientRect() to mean anything.
const HeroCard = forwardRef(function HeroCard({ card, now, venueSplits, splitSlotCount, sessionStats, competitor }, ref) {
  const isRunning = card.status === 'running'
  const isOnline = card.status === 'online'
  const isFlagged = card.status === 'flagged'
  const isFinished = card.status === 'finished'

  const liveElapsed = isRunning && card.startedAt ? (now - new Date(card.startedAt).getTime()) / 1000 : null
  const isStalled = liveElapsed != null && liveElapsed >= STALL_SECONDS

  const statusText = isStalled
    ? 'No update'
    : isOnline
      ? 'On the line'
      : isRunning
        ? 'On the hill'
        : isFlagged
          ? 'Flagged'
          : 'Just finished'

  let clockText = '—'
  if (liveElapsed != null) {
    // Frozen at the cap rather than left to keep climbing - see
    // STALL_SECONDS above.
    clockText = fmtTime(Math.min(liveElapsed, STALL_SECONDS))
  } else if (isFlagged) {
    clockText = 'F'
  } else if (isFinished) {
    clockText = fmtTime(card.finalTime)
  }

  // Purple = fastest so far this session across the whole field, same
  // convention as the results/session tables. Only meaningful once the
  // card actually has a value to compare - assumes the rider is running in
  // whatever the retained `results` topic currently shows as the latest
  // session, since a live `hill` event carries no session code until it
  // finishes.
  const isSessionBestTime =
    isFinished && card.finalTime != null && sessionStats?.bestTime != null && card.finalTime <= sessionStats.bestTime + 0.001
  const isPB =
    card.finalTime != null && isRunPB(competitor?.sessions, card.session, card.finalTime, competitor?.personalBest)
  const priorDelta = isFinished ? priorBestDelta(card, competitor) : null

  // Poll-based (non-NIGAN) venues never emit `hill.split` - timing-bridge's
  // finished/flagged hill events carry a time but no gate data (see
  // MQTT_SCHEMA.md), so card.splits stays empty for their whole run. Once
  // the run is finished/flagged though, that same run's gates are already
  // sitting in the retained `results` snapshot at
  // competitor.sessions[card.session].gates - reuse those instead of
  // showing empty split chips for an entire non-NIGAN event. Only kicks in
  // when card.splits is actually empty, so a NIGAN run's real live splits
  // are never second-guessed by (necessarily slightly staler) results data.
  const finishedGates = card.splits.length === 0 && card.session ? competitor?.sessions?.[card.session]?.gates : null
  const usingFallbackGates = finishedGates != null && finishedGates.length > 0
  const liveSplits = usingFallbackGates
    ? finishedGates.map((g) => ({ gate: g.gate, gateType: g.type, value: g.value }))
    : card.splits

  // Fixed number of split slots, left-to-right in arrival order, so a card
  // never reflows/jumps as new splits land - only the (already-reserved)
  // slot's content changes. Sized off venue.splits when the hill's real
  // split count is known, else a default of 3 (most hills only have a
  // handful - bridge/esses/finish-style).
  const slots = Array.from({ length: splitSlotCount }, (_, i) => liveSplits[i] || null)

  return (
    <div
      ref={ref}
      className={`hero-card ${card.status}${isStalled ? ' stalled' : ''}`}
      style={{ '--split-slots': splitSlotCount }}
    >
      <div className="hero-main">
        <div className="status-text">
          {statusText}
          {/* "That's 0.2s slower than they did in practice" - inline so it
              costs no extra vertical space (this card's height is fixed,
              see index.css) and needs no click-through. */}
          {priorDelta && (
            <>
              {' · '}
              <DeltaVsPrior delta={priorDelta} />
            </>
          )}
        </div>
        <div className="hero-id">
          <span className="hero-num mono">#{card.num}</span>
          <span className="hero-name">{card.rider}</span>
          {/* card.class only ever arrives on the `online` event - if that
              one message got lost (QoS 0, no delivery guarantee) the card
              would otherwise never show a class for its whole lifetime.
              Fall back to the class they're already grouped under in the
              retained `results` topic, which doesn't have that gap. */}
          {(card.class || competitor?.className) && (
            <span className="class-tag">{card.class || competitor?.className}</span>
          )}
        </div>
        {/* Always rendered, even with no machine known yet (poll-based
            venues never send one) - reserves the line's height so the card
            doesn't grow/shrink whenever this shows up or disappears. */}
        <div className="hero-machine">{card.machine || ' '}</div>
      </div>
      <div className="hero-splits">
        {slots.map((s, i) => {
          if (!s) return <span className="split-chip empty" key={i} />
          const venueGate = venueSplits.find((g) => g.gate === s.gate)
          const field = venueGate ? gateKey(venueGate) : null
          const best = field ? sessionStats?.bestAtField?.[field] : null
          const isBest = best != null && (s.gateType === 'S' ? s.value >= best - 0.001 : s.value <= best + 0.001)
          // Fallback gates (see liveSplits above) come straight out of
          // competitor.sessions[card.session] - excluding that code stops
          // this run's own gate values counting as their own "prior" best.
          // A genuinely live NIGAN split has no excludeCode needed - the
          // run's still live, so it isn't in competitor.sessions yet, only
          // its actual prior completed runs are.
          const priorGateBest = field
            ? bestPriorGateValue(competitor?.sessions, usingFallbackGates ? card.session : null, field, s.gateType)
            : null
          const isGatePB =
            !isBest &&
            priorGateBest != null &&
            (s.gateType === 'S' ? s.value >= priorGateBest - 0.001 : s.value <= priorGateBest + 0.001)
          return (
            <span className={`split-chip ${isBest ? 'purple' : isGatePB ? 'pb' : ''}`} key={i}>
              <span className="split-chip-label">{splitLabel(s.gate, s.gateType, venueSplits)}</span>
              {s.gateType === 'S' ? `${s.value?.toFixed(1)} mph` : `${s.value?.toFixed(2)}s`}
            </span>
          )
        })}
      </div>
      {/* purple = fastest this session right now, yellow = this rider's own
          PB (not the outright fastest) - never both at once. */}
      <div className={`clock mono ${isSessionBestTime ? 'purple' : isPB ? 'pb' : ''}`}>
        {clockText}
        {isPB && <span className="pb-pill">PB</span>}
      </div>
    </div>
  )
})

/** Fastest non-flagged Practice time, for when a rider has no Timed best
 * yet (publishers.py's `best` is deliberately Event-only - during Practice
 * every rider legitimately has best:0, i.e. "nothing to show" - but a
 * commentator watching Practice still wants *some* read on pace). */
function bestPracticeTime(sessions) {
  const times = Object.entries(sessions || {})
    .filter(([code, s]) => code.startsWith('P') && s.time)
    .map(([, s]) => s.time)
  return times.length ? Math.min(...times) : null
}

/** Each rider's overall event best (Timed, falling back to Practice - see
 * bestPracticeTime) - used for the per-class standings, which track
 * progress across the whole event, not any one session. */
function eventBest(r) {
  const timedBest = r.best || null
  const practiceBest = timedBest ? null : bestPracticeTime(r.sessions)
  return { timedBest, practiceBest, displayBest: timedBest ?? practiceBest }
}

/**
 * Sorts a competitor list by `getBest(r)` (see eventBest/sessionTimeGetter
 * above), assigning our own `rank`/`interval`/`isPB` rather than trusting
 * the backend's `pos` (which is Timed-only and doesn't reflect Practice
 * pace at all - every rider is `pos: 0` until Timed running starts).
 * `interval` is the gap to the leader (not to the rider immediately
 * ahead), null for the leader and for anyone with no time yet. Riders with
 * no time sort last, by race number.
 */
function rankRows(competitors, getBest) {
  const withTime = []
  const withoutTime = []
  competitors.forEach((r) => {
    const { timedBest, practiceBest, displayBest } = getBest(r)
    const row = { ...r, timedBest, practiceBest, displayBest }
    ;(displayBest != null ? withTime : withoutTime).push(row)
  })
  withTime.sort((a, b) => a.displayBest - b.displayBest)
  withoutTime.sort((a, b) => a.num - b.num)
  const leaderTime = withTime[0]?.displayBest ?? null
  withTime.forEach((r, i) => {
    r.rank = i + 1
    r.interval = i === 0 ? null : r.displayBest - leaderTime
    // No session code: displayBest must beat every run today, not just the
    // historical PB - a Timed best can be slower than an earlier Practice run.
    r.isPB = isRunPB(r.sessions, null, r.displayBest, r.personalBest)
  })
  withoutTime.forEach((r) => {
    r.rank = null
    r.interval = null
    r.isPB = false
  })
  return [...withTime, ...withoutTime]
}

function LeaderboardTable({ label, rows, showClass, live }) {
  // Default expanded (not collapsed) - each table's own independent state,
  // not lifted to ClassLeaderboard, since collapsing one class has no
  // effect on any other section.
  const [collapsed, setCollapsed] = useState(false)
  const toggle = () => setCollapsed((c) => !c)
  return (
    // border-left is always 4px, transparent when not live - a fixed
    // reserved strip that only changes colour, never width, so a class
    // going live/not-live doesn't shift the table next to it (same
    // technique as the hero cards' status-colour border).
    <div className={`class-section ${live ? 'live' : ''}`}>
      <div className="table-wrap">
        <table>
          <thead>
            {/* onClick lives on the row, not just the label button, so the
                whole header bar is a click target - useful on a touchscreen
                kiosk. The button inside still does the real toggling (see
                its own note below) so keyboard/AT users get a real
                interactive element, not just a bare clickable <tr>. */}
            <tr className="lb-toggle-row" onClick={toggle}>
              {/* Label lives IN the Pos column's own header cell (not a
                  colSpan across it and #, and not a separate line above
                  the table) - a spanned <th> covering what the body
                  renders as two separate <td>s breaks table-layout:fixed's
                  per-column width calculation (gap between Pos/#, and
                  every class's table settling on different widths from
                  every other one's). A real per-column header fixes both,
                  and "Pos" itself is a low-value label anyway (an
                  unlabelled first numeric column already reads as rank) -
                  the class name earns that space more. */}
              <th className="lb-rank-col">
                {/* No onClick here - a real <button>'s own Enter/Space
                    activation dispatches a genuine DOM click, which bubbles
                    up to the <tr>'s handler above on its own. Adding a
                    second handler here would double-toggle (open then
                    immediately re-close) on every keyboard/mouse click. */}
                <button type="button" className="lb-toggle" aria-expanded={!collapsed}>
                  <span className="lb-toggle-label">{label}</span>
                  <span className="lb-toggle-arrow">{collapsed ? '▸' : '▾'}</span>
                </button>
              </th>
              <th className="num-col">#</th>
              <th className="rider-col">Rider</th>
              <th className="num-col">Best</th>
              <th className="num-col">Int</th>
            </tr>
          </thead>
          {!collapsed && (
            <tbody>
              {rows.map((r) => (
                // isQualifier only ever set on Overall's rows (see
                // overallRowsWithQualifier) - undefined everywhere else, so
                // per-class tables are unaffected without needing their own
                // prop to opt out.
                //
                // key is num+rider, not just num (same compound key
                // TopTenPage.jsx already uses) - backend keys competitors
                // by (class, race_number), see _build_results, so the same
                // physical race number can legitimately belong to a
                // different rider in a different class. Overall flattens
                // every class into one list, so a bare `num` key could
                // collide across two riders sharing a number in different
                // classes - React would then reuse that row's DOM node
                // (and its qualifier-highlight class) across two genuinely
                // different people as the sort order shifted between them.
                <tr key={`${r.num}-${r.rider}`} className={r.isQualifier ? 'qualifier' : ''}>
                  <td className="lb-rank">{r.rank || ''}</td>
                  <td className="num-col mono">{r.num}</td>
                  {/* Class used to be its own column, shown only on Overall
                      (showClass) - that gave Overall's table one more
                      column than every per-class table, so its Best/Int
                      boundaries never actually lined up with theirs (hidden
                      by the row content between tables when expanded, but
                      glaringly obvious once every table's collapsed down to
                      just its header row sitting directly on the next
                      one's). Every LeaderboardTable now has the exact same
                      column set regardless of showClass, so table-layout:
                      fixed settles on identical widths everywhere - same
                      inline class-tag-next-to-name pattern HeroCard already
                      uses. */}
                  <td className="rider">
                    <span className="name">
                      {r.rider}
                      {showClass && <span className="class-tag">{r.className}</span>}
                    </span>
                    <span className="machine">{r.machine}</span>
                  </td>
                  {/* No purple here - Pos already says who's fastest, a
                      second highlight on Best adds nothing. Yellow (PB)
                      still applies - that's about the rider's own
                      improvement, not rank. A practice-only fallback (no
                      Timed best yet) renders dimmed/italic so it's never
                      mistaken for a real scored result. */}
                  <td className={`split num-col ${r.isPB ? 'pb' : ''} ${r.practiceBest != null ? 'practice-fallback' : ''}`}>
                    {r.displayBest != null ? fmtTime(r.displayBest) : '—'}
                    {r.isPB && <span className="pb-pill">PB</span>}
                  </td>
                  <td className="num-col mono interval-cell">{r.interval != null ? `+${r.interval.toFixed(2)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </div>
  )
}

function ClassLeaderboard({ results, liveClasses, sessionOverrides }) {
  const classes = results?.classes ?? EMPTY_CLASSES
  const orderedClassNames = sortClasses(classes.map((c) => c.className))
  const byClassName = Object.fromEntries(classes.map((c) => [c.className, c]))
  const qualifiers = useMqttTopic('qualifiers')?.qualifiers ?? EMPTY_QUALIFIERS

  // "Overall" = fastest across the whole event so far, cutting across every
  // class - same eventBest() (best Timed run, falling back to Practice
  // until a Timed time exists) the per-class tables already use below, just
  // ranked across the whole field instead of within one class. Used to be
  // scoped to only the single latest session code, which meant every rider
  // who hadn't personally run yet THIS session (e.g. still on T1 while
  // someone else had already started T2) went blank here despite having a
  // perfectly good time on the board - indistinguishable from "hasn't run
  // at all", and just duplicated whatever the Current Session table below
  // already shows for that one session anyway.
  //
  // Deduped by rider name (case-insensitive) - a rider entered under more
  // than one race number/class would otherwise show up as two separate
  // Overall rows, one per entry. Keeps whichever of their entries has the
  // better eventBest so far, same "one rider, their single best" principle
  // backend's _build_qualifiers already applies to the qualifiers list.
  const overallCompetitors = useMemo(() => {
    const byRiderName = new Map()
    classes.forEach((c) => {
      c.competitors.forEach((r) => {
        const key = r.rider.toLowerCase()
        const entry = { ...r, className: c.className }
        const existing = byRiderName.get(key)
        if (!existing) {
          byRiderName.set(key, entry)
          return
        }
        const newBest = eventBest(entry).displayBest
        const oldBest = eventBest(existing).displayBest
        if (newBest != null && (oldBest == null || newBest < oldBest)) byRiderName.set(key, entry)
      })
    })
    return [...byRiderName.values()]
  }, [classes])
  const overallRows = useMemo(() => rankRows(overallCompetitors, eventBest), [overallCompetitors])

  // Top 10 Timed qualifiers (backend's `qualifiers` topic - same list
  // TopTenPage.jsx shows, and same "first 10 array entries" convention it
  // uses for its own `qualified` highlight, not the dense-ranked `pos`
  // value, which can repeat on a tie). Deliberately NOT recomputed from
  // overallRows here - qualifiers is Timed-only (no Practice fallback,
  // unlike eventBest/Overall's own ranking), deduped by rider name across
  // any race number they've used, and excludes 2WD/Morgan - reimplementing
  // that here risks drifting from the one real definition of "who
  // currently qualifies for the Run-Off" (see feedback_single_source_of_
  // truth_points memory).
  const qualifierNames = useMemo(
    () => new Set(qualifiers.slice(0, 10).map((q) => q.rider.toLowerCase())),
    [qualifiers],
  )
  const overallRowsWithQualifier = useMemo(
    () => overallRows.map((r) => ({ ...r, isQualifier: qualifierNames.has(r.rider.toLowerCase()) })),
    [overallRows, qualifierNames],
  )

  // One wrapping element so the whole leaderboard - every class table
  // plus Overall - stays together as a single block wherever it's placed,
  // rather than individual tables getting scattered across two columns.
  // Per-class tables first, Overall last.
  return (
    <div className="commentator-leaderboard">
      {orderedClassNames.map((cls) => (
        <LeaderboardTable
          key={cls}
          label={cls}
          rows={rankRows(byClassName[cls].competitors, eventBest)}
          live={liveClasses.has(cls)}
        />
      ))}
      {/* Just "Overall" - the Pos column header is only 54px wide (see
          index.css), no room for anything more descriptive without
          truncating badly. */}
      <LeaderboardTable label="Overall" rows={overallRowsWithQualifier} showClass live={liveClasses.size > 0} />
    </div>
  )
}

function CurrentSessionTable({ sessionCodes, latestSession, viewedSession, onPickSession, rows, cols, sessionStats, sessionOverrides }) {
  if (!sessionCodes.length) {
    return (
      <section className="commentator-session-table">
        <div className="section-head">
          <h2>Current Session</h2>
        </div>
        <div className="empty-state">Waiting for the first run…</div>
      </section>
    )
  }

  return (
    <section className="commentator-session-table">
      <div className="section-head">
        <h2>Current Session</h2>
        <p>{sessionLabel(viewedSession, sessionOverrides)}</p>
      </div>
      {/* The latest session's button is deliberately bigger/more prominent
          (.session-switch .chip-current) than the others - it's the one
          state a commentator needs to find instantly without reading
          every label, everything else is a deliberate look-back. */}
      <div className="session-switch">
        {sessionCodes.map((code) => (
          <button
            key={code}
            className={`chip ${code === latestSession ? 'chip-current' : ''} ${viewedSession === code ? 'active' : ''}`}
            onClick={() => onPickSession(code)}
          >
            {code === latestSession ? `Current — ${sessionLabel(code, sessionOverrides)}` : sessionLabel(code, sessionOverrides)}
          </button>
        ))}
      </div>
      {/* Explicit definite pixel width, same technique (and same reason -
          see index.css's own comment on the mobile `table` rule) as
          ResultsPage.jsx's --mobile-table-width: this table sits inside a
          flex item sized by its own content (flex: 0 1 auto), and `width:
          100%` inside an auto-sized flex item is a genuinely circular case
          browsers resolve inconsistently - it was inflating this table's
          hypothetical flex-basis enormously, which then starved Standings
          (its sibling in the same row) down to a sliver few pixels wide on
          wide screens. A real number here breaks that circularity outright,
          for every viewport, not just mobile - must match the actual
          column widths below (num-col 64, rider-col 220, class-col 130,
          one num-col per real split, then Time/vs Prior at 64 each). */}
      <div className="table-wrap">
        <table style={{ width: 64 + 220 + 130 + cols.length * 64 + 64 + 64 }}>
          <thead>
            <tr>
              <th className="num-col">#</th>
              <th className="rider-col">Rider</th>
              <th className="class-col">Class</th>
              {cols.map((c) => (
                <th key={c.field} className="num-col">
                  {c.label}
                </th>
              ))}
              <th className="num-col">Time</th>
              <th className="num-col">vs Prior</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const t = fmtSessionTime(r.sessions[viewedSession])
              const isBestTime = t.value != null && sessionStats.bestTime != null && t.value <= sessionStats.bestTime + 0.001
              const isPB = isRunPB(r.sessions, viewedSession, t.value, r.personalBest)
              // Same "vs their own best from another session today" delta
              // as the hero card - there's room for it here too, and it's
              // useful for every row, not just whoever's currently in the
              // hero panel.
              const priorDelta = bestPriorSessionDelta(r.sessions, viewedSession, t.value)
              return (
                <tr key={r.num}>
                  <td className="num-col mono">{r.num}</td>
                  <td className="rider">
                    <span className="name">{r.rider}</span>
                    <span className="machine">{r.machine}</span>
                  </td>
                  <td>
                    <span className="class-tag">{r.className}</span>
                  </td>
                  {cols.map((c) => {
                    const v = gateValueFor(r.sessions[viewedSession], c.field)
                    const bestForField = sessionStats.bestAtField[c.field]
                    const isBestGate =
                      v != null &&
                      bestForField != null &&
                      (c.type === 'S' ? v >= bestForField - 0.001 : v <= bestForField + 0.001)
                    const priorGateBest = bestPriorGateValue(r.sessions, viewedSession, c.field, c.type)
                    const isGatePB =
                      !isBestGate &&
                      v != null &&
                      priorGateBest != null &&
                      (c.type === 'S' ? v >= priorGateBest - 0.001 : v <= priorGateBest + 0.001)
                    return (
                      <td key={c.field} className={`split num-col ${isBestGate ? 'purple' : isGatePB ? 'pb' : ''}`}>
                        {/* === 'S' not === 'T' - NIGAN's Finish-beam gate (type 'F')
                            duplicates the run's finish time and must render like one
                            (2dp), not fall into the 1dp branch meant for speed traps. */}
                        {v != null ? v.toFixed(c.type === 'S' ? 1 : 2) : '·'}
                      </td>
                    )
                  })}
                  <td className={`split num-col ${isBestTime ? 'purple' : isPB ? 'pb' : ''} ${t.flagged ? 'flagged' : ''}`}>
                    {t.text}
                    {isPB && <span className="pb-pill">PB</span>}
                  </td>
                  <td className="num-col mono interval-cell">
                    <DeltaVsPrior delta={priorDelta} />
                    {!priorDelta && '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default function CommentatorPage() {
  const connected = useMqttConnected()
  const venue = useMqttTopic('venue')
  const results = useMqttTopic('results')
  const sessionOverrides = useMqttTopic('sessions')?.overrides
  const activity = useHillActivity()
  const venueSplits = venue?.splits ?? EMPTY_SPLITS

  // Single shared clock tick for every running card, rather than one
  // setInterval per card - costs nothing while nobody's on the hill. Uses
  // Date.now() directly rather than the `now` state below (reading `now`
  // here would be circular) - fine because every activity change already
  // triggers a render, and once ticking, each 100ms tick re-evaluates this
  // itself, so a card crossing STALL_SECONDS self-stops the interval
  // within one tick of no genuinely-running card being left, rather than
  // ticking forever for a clock that's now frozen and won't change again.
  const anyRunning = activity.some(
    (c) => c.status === 'running' && (!c.startedAt || Date.now() - new Date(c.startedAt).getTime() < STALL_SECONDS * 1000)
  )
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!anyRunning) return
    const id = setInterval(() => setNow(Date.now()), 100)
    return () => clearInterval(id)
  }, [anyRunning])

  const classes = results?.classes ?? EMPTY_CLASSES
  const competitorByNum = useMemo(() => {
    const map = {}
    // className isn't on the competitor object itself in `results` (it's
    // implied by which classes[] group they're nested under) - attach it
    // here so it's usable as a fallback wherever a card's own class (only
    // ever sent on the `online` event) might be missing.
    classes.forEach((c) => c.competitors.forEach((r) => (map[r.num] = { ...r, className: c.className })))
    return map
  }, [classes])

  // "Live" = someone from that class is actually on the hill RIGHT NOW
  // (online/running), not "someone from that class has appeared at any
  // point this event" - `activity` never prunes finished/flagged cards
  // (see useHillActivity's own docs for why), so without this status
  // filter every class that had ever run would stay marked live forever,
  // which is exactly what made the left-hand indicator bar look permanently
  // lit on every table after only a couple of pairs had gone.
  const liveClasses = useMemo(
    () =>
      new Set(
        activity
          .filter((c) => c.status === 'online' || c.status === 'running')
          .map((c) => c.class || competitorByNum[c.num]?.className)
          .filter(Boolean),
      ),
    [activity, competitorByNum],
  )

  // Latest session code - drives the hero cards' purple/PB highlighting
  // and the Overall standings, which should always reflect what's
  // actually live regardless of what the commentator is browsing below.
  const sessionCodes = useMemo(() => {
    const codes = new Set()
    classes.forEach((c) => c.competitors.forEach((r) => Object.keys(r.sessions || {}).forEach((code) => codes.add(code))))
    return sortSessionCodes([...codes])
  }, [classes])
  const session = sessionCodes[sessionCodes.length - 1]
  const { rows: sessionRows, cols: sessionCols, stats: sessionStats } = useSessionData(classes, session, venueSplits)

  // Which session the commentator has chosen to VIEW in the session table
  // (see the session-switch buttons in CurrentSessionTable) - defaults to
  // (and self-heals back to, if the picked one stops existing) the latest
  // session, same derive-in-render pattern as ResultsPage.jsx's own
  // session picker.
  const [pickedSession, setPickedSession] = useState(null)
  const viewedSession = pickedSession && sessionCodes.includes(pickedSession) ? pickedSession : session
  const {
    rows: viewedSessionRows,
    cols: viewedSessionCols,
    stats: viewedSessionStats,
  } = useSessionData(classes, viewedSession, venueSplits)

  // Most hills only have a handful of splits (bridge/esses/finish-style) -
  // default to 3 fixed slots when the venue's real count isn't known yet
  // (e.g. dev sim), so cards don't grow extra columns as splits arrive.
  const splitSlotCount = venueSplits.length > 0 ? venueSplits.length : 3

  // Exactly 2 hero slots, always - so the leaderboard/session table below
  // never jumps up or down as riders come and go.
  const MAX_HERO_CARDS = 2
  // Pure arrival order (matches useHillActivity's own ordering exactly),
  // NEVER re-sorted by status - two riders that are BOTH already shown
  // must keep their relative top/bottom positions no matter which one's
  // status changes next (e.g. one finishing while the other's still
  // running). An earlier version sorted active-before-finished here,
  // which reshuffled the two of them the instant either one's status
  // changed - looked like a card's content randomly disappearing/
  // reappearing in the other slot mid-run.
  const shown = activity.slice(0, MAX_HERO_CARDS)

  let heroSlots
  if (shown.length === MAX_HERO_CARDS) {
    heroSlots = shown.map((card) => ({ card }))
  } else if (shown.length === 1) {
    const [only] = shown
    const isActive = only.status === 'online' || only.status === 'running'
    // A placeholder only ever gets positioned relative to the ONE other
    // real card here, never used to reorder two real cards against each
    // other (see above) - an active rider keeps the top slot (still
    // genuinely current), but a lingering finished/flagged result cedes
    // it to "waiting" instead, since that's stale info and a placeholder
    // more honestly represents "nothing new yet".
    heroSlots = isActive ? [{ card: only }, { card: null }] : [{ card: null }, { card: only }]
  } else {
    // Nothing at all - one placeholder spanning the full area
    // (.hero-placeholder-full, index.css) rather than two identical
    // stacked "Waiting for next competitor…" boxes saying the same thing
    // twice, without changing .hero-cards' overall height either way.
    heroSlots = [{ card: null, full: true }]
  }

  // FLIP animation: when a card moves between the top and bottom slot (a
  // new arrival pushing someone down, or a finish freeing the top slot),
  // animate the move instead of letting it just jump - makes the reorder
  // itself legible instead of two cards silently swapping content.
  // (prefers-reduced-motion already zeroes every transition-duration
  // app-wide, see index.css's global rule, so this degrades to an instant
  // jump automatically for anyone who's asked for that.)
  const heroCardsRef = useRef(null)
  const cardRefs = useRef(new Map())
  const prevTops = useRef(new Map())
  // Gate this effect on the shown riders' actual order, not "every render" -
  // this component also re-renders every 100ms while anyone's running (the
  // shared clock tick) and on every results/venue topic update, neither of
  // which ever moves a card. Re-running the measurement on those unrelated
  // renders was actively harmful, not just wasteful: while a FLIP
  // transition is still mid-flight, getBoundingClientRect() reports the
  // CURRENT INTERPOLATED position, not the transition's final target - so
  // an unrelated 100ms-tick render landing mid-animation would see "the
  // position changed" (it had, by whatever fraction the transition had
  // covered so far), decide that was a fresh reorder, and restart the FLIP
  // from that half-finished point - every single tick, for as long as
  // anything was running. That's a self-reinforcing stutter/flashing loop,
  // not a one-off glitch - the same failure family as the ambient-page-
  // reflow flashing this effect already had to guard against once (see the
  // containerTop comment below), just triggered by its own animation
  // instead of the page's.
  const shownKey = shown.map((c) => c.num).join(',')
  useLayoutEffect(() => {
    // Position relative to .hero-cards itself, NOT viewport-absolute
    // getBoundingClientRect().top - the page's overall height keeps
    // shifting slightly as the results/session tables update live, which
    // moves everything below it (including .hero-cards) up and down on
    // the page even though a card's position *within* .hero-cards hasn't
    // actually changed. Using the raw viewport top meant that ambient
    // page reflow alone could look like "the card moved," re-triggering
    // the animation over and over - a stuck-looking flashing loop on a
    // card that was never actually reordered.
    const containerTop = heroCardsRef.current?.getBoundingClientRect().top ?? 0
    const nextTops = new Map()
    cardRefs.current.forEach((el, num) => {
      if (!el) return
      const top = el.getBoundingClientRect().top - containerTop
      nextTops.set(num, top)
      const prevTop = prevTops.current.get(num)
      if (prevTop != null && Math.abs(prevTop - top) > 1) {
        el.style.transition = 'none'
        el.style.transform = `translateY(${prevTop - top}px)`
        el.getBoundingClientRect() // force reflow before releasing the transform
        el.style.transition = 'transform 0.4s ease'
        el.style.transform = ''
      }
    })
    prevTops.current = nextTops
  }, [shownKey])

  return (
    <div className="kiosk-page">
      <div className={`kiosk-status ${connected ? '' : 'offline'}`}>
        <span className={`live-dot ${connected ? '' : 'offline'}`} />
        {venue?.venue || 'Waiting for event…'}
        {venue?.date && ` · ${venue.date}`}
      </div>

      <section className="hero-cards" ref={heroCardsRef}>
        {heroSlots.map((slot, i) =>
          slot.card ? (
            <HeroCard
              key={slot.card.num}
              ref={(el) => {
                if (el) cardRefs.current.set(slot.card.num, el)
                else cardRefs.current.delete(slot.card.num)
              }}
              card={slot.card}
              now={now}
              venueSplits={venueSplits}
              splitSlotCount={splitSlotCount}
              sessionStats={sessionStats}
              competitor={competitorByNum[slot.card.num]}
            />
          ) : (
            <div className={`hero-card-placeholder ${slot.full ? 'hero-placeholder-full' : ''}`} key={`ph-${i}`}>
              Waiting for next competitor…
            </div>
          ),
        )}
      </section>

      <div className="commentator-lower">
        <CurrentSessionTable
          sessionCodes={sessionCodes}
          latestSession={session}
          viewedSession={viewedSession}
          onPickSession={setPickedSession}
          rows={viewedSessionRows}
          cols={viewedSessionCols}
          sessionStats={viewedSessionStats}
          sessionOverrides={sessionOverrides}
        />
        <ClassLeaderboard results={results} liveClasses={liveClasses} sessionOverrides={sessionOverrides} />
      </div>
    </div>
  )
}
